// Scripts/Geolocation.js
// ======================
// زر التحكم: #goToCoordinatesBtn
// السلوك: متابعة تلقائية حتى أي تفاعل يدوي (Pan/Zoom) -> نوقف التتبّع ونُبقي الموقع ظاهر.

import Feature from 'ol/Feature';
import Geolocation from 'ol/Geolocation';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';

let map = null;
try { map = $('#map').data('map'); } catch { }
if (!map || typeof map.getView !== 'function') {
  console.error('Geolocation: map object not found. Aborting init.');
  window.GeoPST_Geolocation = { start: () => { }, stop: () => { }, toggle: () => { }, isActive: () => false, destroy: () => { } };
} else {
  const view = map.getView();
  const mapProjection = view.getProjection();

  function canUseGeolocationHere() {
    const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname.endsWith('.localhost');
    const isSecure = window.isSecureContext === true;
    return isSecure || isLocalhost;
  }

  const geolocation = new Geolocation({
    tracking: false,
    projection: mapProjection,
    trackingOptions: { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
  });

  const ACCURACY_THRESHOLD = 50;

  const accuracyFeature = new Feature();
  const positionFeature = new Feature();

  function getDotSvg(isHighAccuracy = true) {
    const coreColor = isHighAccuracy ? '#005CE6' : '#70757A';
    return `
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="14" fill="rgba(0, 0, 0, 0.20)" />
        <circle cx="20" cy="20" r="11" fill="#FFFFFF" />
        <circle cx="20" cy="20" r="8" fill="${coreColor}" />
      </svg>
    `;
  }

  function createDotStyles(isHighAccuracy = true) {
    const svgDataUri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(getDotSvg(isHighAccuracy));
    return [new Style({ image: new Icon({ src: svgDataUri, scale: 1, anchor: [0.5, 0.5], rotateWithView: false }) })];
  }

  const highAccuracyStyles = createDotStyles(true);
  const lowAccuracyStyles = createDotStyles(false);

  const getHeadingSvg = (isHighAccuracy = true) => {
    const color = isHighAccuracy ? '0, 92, 230' : '112, 117, 122';
    return `
      <svg width="120" height="120" viewBox="-10 -20 140 140" xmlns="http://www.w3.org/2000/svg">
        <defs><linearGradient id="beam" x1="50%" y1="50%" x2="50%" y2="-10%"><stop offset="0%" style="stop-color:rgba(${color}, 0.85);" /><stop offset="45%" style="stop-color:rgba(${color}, 0.35);" /><stop offset="100%" style="stop-color:rgba(${color}, 0);" /></linearGradient></defs>
        <path d="M 60 60 L 32 22 Q 60 2 88 22 Z" fill="url(#beam)" />
      </svg>
    `;
  };

  let deviceHeading = null;
  let deviceOrientationInitialized = false;

  let accuracyListener = null;
  let positionListener = null;
  let headingListener = null;
  let errorListener = null;
  let moveStartListener = null;
  let onceListener = null;
  let safetyTimer = null;
  let docClickHandler = null;

  function handleDeviceOrientation(event) {
    let heading = null;
    if (event.webkitCompassHeading !== undefined) heading = event.webkitCompassHeading;
    else if (event.absolute === true && event.alpha !== null) heading = 360 - event.alpha;
    else if (event.alpha !== null) heading = 360 - event.alpha;

    if (heading !== null) {
      deviceHeading = heading * (Math.PI / 180);
      if (geolocation.getTracking() && positionFeature) positionFeature.changed();
    }
  }

  function initDeviceOrientation(forceRequest = false) {
    if (deviceOrientationInitialized && !forceRequest) return;
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission().then(permissionState => {
        if (permissionState === 'granted') {
          window.addEventListener('deviceorientation', handleDeviceOrientation, false);
          deviceOrientationInitialized = true;
        } else {
          console.warn('Device orientation permission denied');
          notifyUser('تم تعطيل البوصلة');
          deviceOrientationInitialized = true;
        }
      }).catch(err => console.error('DeviceOrientationEvent.requestPermission error:', err));
    } else {
      if ('ondeviceorientationabsolute' in window) window.addEventListener('deviceorientationabsolute', handleDeviceOrientation, false);
      else window.addEventListener('deviceorientation', handleDeviceOrientation, false);
      deviceOrientationInitialized = true;
    }
  }

  const highAccuracyHeadingIcon = new Icon({ src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(getHeadingSvg(true)), scale: 1, anchor: [0.5, 0.5], opacity: 0.8, rotateWithView: true });
  const lowAccuracyHeadingIcon = new Icon({ src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(getHeadingSvg(false)), scale: 1, anchor: [0.5, 0.5], opacity: 0.8, rotateWithView: true });

  const headingStyle = new Style({});

  positionFeature.setStyle(function () {
    const accuracy = geolocation.getAccuracy() || 0;
    const isGoodSignal = accuracy <= ACCURACY_THRESHOLD;
    const activeDotStyles = isGoodSignal ? highAccuracyStyles : lowAccuracyStyles;
    const heading = deviceHeading !== null ? deviceHeading : geolocation.getHeading();

    if (heading !== undefined && heading !== null && !isNaN(heading)) {
      const activeHeadingIcon = isGoodSignal ? highAccuracyHeadingIcon : lowAccuracyHeadingIcon;
      activeHeadingIcon.setRotation(heading);
      headingStyle.setImage(activeHeadingIcon);
      return [headingStyle, ...activeDotStyles];
    }
    return activeDotStyles;
  });

  const geoSource = new VectorSource({ features: [accuracyFeature, positionFeature] });

  // ✅ Caching Accuracy Circle Styles (التحسين النهائي من Gemini)
  const goodAccuracyStyle = new Style({
    fill: new Fill({ color: 'rgba(0, 92, 230, 0.05)' }),
    stroke: new Stroke({ color: 'rgba(0, 92, 230, 0.25)', width: 1 })
  });
  const badAccuracyStyle = new Style({
    fill: new Fill({ color: 'rgba(112, 117, 122, 0.05)' }),
    stroke: new Stroke({ color: 'rgba(112, 117, 122, 0.25)', width: 1 })
  });

  let geoLayer = map.getLayers().getArray().find(l => l && l.get('name') === 'GeolocationLayer');
  if (!geoLayer) {
    geoLayer = new VectorLayer({
      source: geoSource,
      properties: { name: 'GeolocationLayer', internal: true, uiHidden: true },
      zIndex: 9999,
      style: (feature) => {
        if (feature === accuracyFeature) {
          const accuracy = geolocation.getAccuracy() || 0;
          return (accuracy <= ACCURACY_THRESHOLD) ? goodAccuracyStyle : badAccuracyStyle;
        }
        return undefined;
      }
    });
    geoLayer.setVisible(false);
    map.addLayer(geoLayer);
  } else {
    // 🛡️ Fix: Ensure existing layer uses the fresh source/features from this closure
    geoLayer.setSource(geoSource);
    // 🛡️ Fix: Ensure style function is updated to use the fresh features
    geoLayer.setStyle((feature) => {
      if (feature === accuracyFeature) {
        const accuracy = geolocation.getAccuracy() || 0;
        return (accuracy <= ACCURACY_THRESHOLD) ? goodAccuracyStyle : badAccuracyStyle;
      }
      return undefined;
    });
  }

  const gpsButton = document.getElementById('goToCoordinatesBtn') || document.querySelector('[data-tool="geolocation"]');

  function setUI(active, following = false) {
    const currentBtn = document.getElementById('goToCoordinatesBtn') || document.querySelector('[data-tool="geolocation"]');
    if (currentBtn) {
      currentBtn.classList.toggle('active', active);
      currentBtn.classList.toggle('following', following);
      currentBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  function notifyUser(msg) {
    if (window && typeof window.showErrorModal === 'function') return window.showErrorModal(msg);
    if (window && window.Swal && typeof window.Swal.fire === 'function') return window.Swal.fire('تنبيه', msg, 'warning');
    alert(msg);
  }

  let isProgrammaticViewChange = false;
  function centerProgrammatic(pos, zoomTo = null) {
    isProgrammaticViewChange = true;

    // صمام أمان في حال تم قطع حركة الخريطة من قبل المستخدم
    if (safetyTimer) clearTimeout(safetyTimer);
    safetyTimer = setTimeout(() => { isProgrammaticViewChange = false; }, 1500);

    const anim = { center: pos, duration: 300 };
    if (zoomTo != null) anim.zoom = zoomTo;
    view.animate(anim, () => {
      clearTimeout(safetyTimer);
      isProgrammaticViewChange = false;
    });
  }

  let isFollowing = false;

  function clearGeolocationGraphics() {
    accuracyFeature.setGeometry(null);
    positionFeature.setGeometry(null);
  }

  function stopTrackingKeepUI() {
    isFollowing = false;
    setUI(true, false);
    geoLayer.setVisible(true);
  }

  function stopTrackingFull() {
    geolocation.setTracking(false);
    isFollowing = false;
    setUI(false, false);
    geoLayer.setVisible(false);
    clearGeolocationGraphics();
  }

  function startTrackingAndFollow() {
    if (!('geolocation' in navigator)) { notifyUser('متصفحك لا يدعم تحديد الموقع.'); return; }
    if (!canUseGeolocationHere()) { notifyUser('لا يمكن طلب الموقع لأن الصفحة ليست على HTTPS.'); return; }

    geolocation.setTracking(true);
    isFollowing = true;
    setUI(true, true);
    geoLayer.setVisible(true);

    const p = geolocation.getPosition();
    if (p) {
      positionFeature.setGeometry(new Point(p));
      centerProgrammatic(p, Math.max(view.getZoom() || 0, 11));
    } else {
      onceListener = () => {
        const first = geolocation.getPosition();
        if (first) {
          positionFeature.setGeometry(new Point(first));
          centerProgrammatic(first, Math.max(view.getZoom() || 0, 11));
        }
        // إزالة الـ Listener سواء وجدنا الموقع أو لم نجده لمنع تسرب الذاكرة
        if (onceListener) geolocation.un('change:position', onceListener);
        onceListener = null;
      };
      geolocation.on('change:position', onceListener);
    }

    if (window.appTools && typeof window.appTools.requestExclusive === 'function') {
      window.appTools.requestExclusive('geolocation');
    }
  }

  function setActiveTracking(active) {
    if (active) startTrackingAndFollow();
    else stopTrackingFull();
  }

  accuracyListener = () => { const g = geolocation.getAccuracyGeometry(); accuracyFeature.setGeometry(g ? g.clone() : null); };
  geolocation.on('change:accuracyGeometry', accuracyListener);

  positionListener = () => {
    const p = geolocation.getPosition();
    if (!p) return;
    positionFeature.setGeometry(new Point(p));
    positionFeature.changed(); // 🛡️ Force immediate re-render of the blue dot
    if (geolocation.getTracking() && isFollowing) centerProgrammatic(p);
  };
  geolocation.on('change:position', positionListener);

  headingListener = () => { positionFeature.changed(); };
  geolocation.on('change:heading', headingListener);

  errorListener = (error) => {
    console.warn('Geolocation error:', error?.code, error?.message);
    if (error?.code === 3) return;
    stopTrackingFull();
    if (error?.code === 1) notifyUser('تم رفض الوصول للموقع. يرجى تفعيل الصلاحيات من إعدادات المتصفح.');
    else notifyUser('خطأ في تحديد الموقع: ' + (error?.message || 'غير معروف'));
  };
  geolocation.on('error', errorListener);

  moveStartListener = () => {
    if (geolocation.getTracking() && !map.getView().getAnimating()) stopTrackingKeepUI();
  };
  map.on('movestart', moveStartListener);

  // استخدام Event Delegation لضمان عمل الزر حتى لو تم تحميله متأخراً في الـ DOM
  docClickHandler = (e) => {
    const btn = e.target.closest('#goToCoordinatesBtn') || e.target.closest('[data-tool="geolocation"]');
    if (!btn) return;

    initDeviceOrientation(true);
    if (!geolocation.getTracking()) setActiveTracking(true);
    else if (!isFollowing) startTrackingAndFollow();
    else setActiveTracking(false);
  };
  document.addEventListener('click', docClickHandler);

  function destroy() {
    stopTrackingFull();
    if (safetyTimer) clearTimeout(safetyTimer);
    if (onceListener) { geolocation.un('change:position', onceListener); onceListener = null; }
    if (docClickHandler) {
      document.removeEventListener('click', docClickHandler);
      docClickHandler = null;
    }
    if (accuracyListener) geolocation.un('change:accuracyGeometry', accuracyListener);
    if (positionListener) geolocation.un('change:position', positionListener);
    if (headingListener) geolocation.un('change:heading', headingListener);
    if (errorListener) geolocation.un('error', errorListener);
    if (moveStartListener) map.un('movestart', moveStartListener);
    if (deviceOrientationInitialized) {
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
      window.removeEventListener('deviceorientationabsolute', handleDeviceOrientation);
      deviceOrientationInitialized = false;
    }
    if (geoLayer && map) {
      map.removeLayer(geoLayer);
      geoLayer = null;
    }
    window.GeoPST_Geolocation = null;
  }

  window.GeoPST_Geolocation = {
    destroy: destroy,
    start: () => setActiveTracking(true),
    stop: () => setActiveTracking(false),
    toggle: () => setActiveTracking(!geolocation.getTracking()),
    stopFollowing: () => { isFollowing = false; if (geolocation.getTracking()) setUI(true, false); },
    isActive: () => !!geolocation.getTracking(),
    getPosition: () => geolocation.getPosition(),
    getAccuracy: () => geolocation.getAccuracy(),
    getHeading: () => (deviceHeading !== null ? deviceHeading : geolocation.getHeading()),
    on: (type, listener) => geolocation.on(type, listener),
    un: (type, listener) => geolocation.un(type, listener),
    getIsHighAccuracy: () => (geolocation.getAccuracy() || 0) <= ACCURACY_THRESHOLD
  };
}